import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { apiGet } from "../lib/api";
import { formatCurrency, formatDateFr } from "../lib/format";
import { BILAN_2024 } from "../lib/n1-2024";
import { AccountDrilldown } from "../components/drilldown-row";
import { useDateStore } from "../stores/date-store";

export const Route = createFileRoute("/balance")({
  component: BalancePage,
});

interface AccountInLine {
  accountCode: string;
  accountLabel: string;
  total: number;
  kind?: "gross" | "amort";
}

interface ActifLine {
  key: string;
  label: string;
  brut: number;
  amort: number;
  net: number;
  accounts: AccountInLine[];
}

interface ActifSubsection {
  key: string;
  label: string;
  brut: number;
  amort: number;
  net: number;
  lines: ActifLine[];
}

interface ActifSection {
  key: string;
  label: string;
  brut: number;
  amort: number;
  net: number;
  subsections: ActifSubsection[];
}

interface PassifLine {
  key: string;
  label: string;
  total: number;
  accounts: AccountInLine[];
}

interface PassifSection {
  key: string;
  label: string;
  total: number;
  lines: PassifLine[];
}

interface BalanceData {
  asOfDate: string;
  fromDate: string;
  actif: { sections: ActifSection[]; total: number };
  passif: { sections: PassifSection[]; total: number };
  balanced: boolean;
  imbalance: number;
  unmatched: { accountCode: string; accountLabel: string; total: number }[];
}

// N-1 hardcodé 2024 — affiché uniquement quand le bilan est au 31/12/2025 (sinon non pertinent).
const N1_ACTIF_LINES: Record<string, number> = {
  "concessions-brevets": BILAN_2024.concessionsBrevetsNet,
  "autres-immo-corpo": BILAN_2024.autresImmoCorporellesNet,
  "autres-immo-finan": BILAN_2024.autresImmoFinancieres,
  "creances-clients": BILAN_2024.creancesClients,
  disponibilites: BILAN_2024.disponibilites,
};
const N1_PASSIF_LINES: Record<string, number> = {
  capital: BILAN_2024.capital,
  primes: BILAN_2024.primesEmission,
  "reserve-legale": BILAN_2024.reserveLegale,
  ran: BILAN_2024.reportANouveau,
  resultat: BILAN_2024.resultatExercice,
  "emprunts-obligataires-c": BILAN_2024.empruntsObligatairesConvertibles,
  "emprunts-credit": BILAN_2024.empruntsCredit,
  "emprunts-divers": BILAN_2024.empruntsFinanciersDivers,
  "dettes-fournisseurs": BILAN_2024.dettesFournisseurs,
  "dettes-fiscales-sociales": BILAN_2024.dettesFiscalesSociales,
  "autres-dettes": BILAN_2024.autresDettes,
};

function BalancePage() {
  // Le bilan est arrêté à la date de fin de la période globale.
  const asOfDate = useDateStore((s) => s.endDate);

  const { data, isPending, isFetching, error } = useQuery<BalanceData>({
    queryKey: ["balance-sheet", asOfDate],
    queryFn: () => apiGet<BalanceData>(`/api/reports/balance-sheet?asOfDate=${asOfDate}`),
  });

  if (isPending) {
    return <div className="flex items-center justify-center h-64 text-gray-500">Chargement...</div>;
  }
  if (error || !data) {
    return <div className="flex items-center justify-center h-64 text-red-500">Erreur de chargement</div>;
  }

  // Drilldown des comptes : depuis l'ouverture de l'exercice si des
  // à-nouveaux existent (fromDate renvoyé par l'API), sinon cumul depuis toujours.
  const drilldownStart = data.fromDate;
  const drilldownEnd = asOfDate;

  // N-1 hardcodé 2024 dispo uniquement si on regarde le bilan au 31/12/2025.
  const showN1 = asOfDate === "2025-12-31";
  const n1TotalActif = showN1 ? BILAN_2024.totalActif : undefined;
  const n1TotalPassif = showN1 ? BILAN_2024.totalPassif : undefined;

  return (
    <div className="space-y-4 max-w-6xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Bilan au {formatDateFr(asOfDate)}</h1>
        <span className="text-sm text-gray-500">
          Date d'arrêté = fin de la période globale
          {isFetching && " · actualisation…"}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <SummaryCard variant="blue" label="Total Actif" value={formatCurrency(data.actif.total)} />
        <SummaryCard variant="blue" label="Total Passif" value={formatCurrency(data.passif.total)} />
        <SummaryCard
          variant={data.balanced ? "green" : "red"}
          label="Équilibre"
          value={data.balanced ? "✓ Équilibré" : `Δ ${formatCurrency(data.imbalance)}`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* ACTIF */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-green-100 border-b-2 border-green-300 px-4 py-2">
            <h2 className="text-lg font-bold text-green-900">ACTIF</h2>
          </div>
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-500 uppercase">Libellé</th>
                <th className="px-3 py-1.5 text-right text-xs font-semibold text-gray-500 uppercase">Brut</th>
                <th className="px-3 py-1.5 text-right text-xs font-semibold text-gray-500 uppercase">Amort.</th>
                <th className="px-3 py-1.5 text-right text-xs font-semibold text-gray-500 uppercase">Net N</th>
                <th className="px-3 py-1.5 text-right text-xs font-semibold text-gray-500 uppercase">N-1</th>
              </tr>
            </thead>
            <tbody>
              {data.actif.sections.map((section) => (
                <ActifSectionRows
                  key={section.key}
                  section={section}
                  startDate={drilldownStart}
                  endDate={drilldownEnd}
                  showN1={showN1}
                />
              ))}
              <tr className="bg-green-50 border-t-2 border-green-300">
                <td className="px-3 py-2 font-bold text-green-900">TOTAL GÉNÉRAL ACTIF</td>
                <td className="px-3 py-2 text-right text-xs text-gray-500">
                  {formatCurrency(data.actif.sections.reduce((s, sect) => s + sect.brut, 0))}
                </td>
                <td className="px-3 py-2 text-right text-xs text-gray-500">
                  {formatCurrency(data.actif.sections.reduce((s, sect) => s + sect.amort, 0))}
                </td>
                <td className="px-3 py-2 text-right font-bold text-green-900">
                  {formatCurrency(data.actif.total)}
                </td>
                <td className="px-3 py-2 text-right text-sm text-gray-600">
                  {n1TotalActif !== undefined ? formatCurrency(n1TotalActif) : "-"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* PASSIF */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-blue-100 border-b-2 border-blue-300 px-4 py-2">
            <h2 className="text-lg font-bold text-blue-900">PASSIF</h2>
          </div>
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-500 uppercase">Libellé</th>
                <th className="px-3 py-1.5 text-right text-xs font-semibold text-gray-500 uppercase">Net N</th>
                <th className="px-3 py-1.5 text-right text-xs font-semibold text-gray-500 uppercase">N-1</th>
              </tr>
            </thead>
            <tbody>
              {data.passif.sections.map((section) => (
                <PassifSectionRows
                  key={section.key}
                  section={section}
                  startDate={drilldownStart}
                  endDate={drilldownEnd}
                  showN1={showN1}
                />
              ))}
              <tr className="bg-blue-50 border-t-2 border-blue-300">
                <td className="px-3 py-2 font-bold text-blue-900">TOTAL GÉNÉRAL PASSIF</td>
                <td className="px-3 py-2 text-right font-bold text-blue-900">
                  {formatCurrency(data.passif.total)}
                </td>
                <td className="px-3 py-2 text-right text-sm text-gray-600">
                  {n1TotalPassif !== undefined ? formatCurrency(n1TotalPassif) : "-"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {data.unmatched.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
          <h3 className="text-sm font-semibold text-yellow-900 mb-2">
            Comptes non mappés au bilan ({data.unmatched.length})
          </h3>
          <table className="text-xs">
            <tbody>
              {data.unmatched.map((a) => (
                <tr key={a.accountCode}>
                  <td className="font-mono pr-3 py-0.5">{a.accountCode}</td>
                  <td className="pr-3 py-0.5">{a.accountLabel}</td>
                  <td className="text-right py-0.5">{formatCurrency(a.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ActifSectionRows({
  section,
  startDate,
  endDate,
  showN1,
}: {
  section: ActifSection;
  startDate: string;
  endDate: string;
  showN1: boolean;
}) {
  return (
    <>
      <tr className="bg-green-50">
        <td className="px-3 py-1.5 font-bold text-green-900 uppercase text-sm" colSpan={5}>
          {section.label}
        </td>
      </tr>
      {section.subsections.map((sub) => (
        <ActifSubsectionRows
          key={sub.key}
          subsection={sub}
          startDate={startDate}
          endDate={endDate}
          showN1={showN1}
        />
      ))}
      <tr className="bg-green-50/50 border-t border-green-200">
        <td className="px-3 py-1 pl-3 font-semibold text-green-900 text-sm">
          Total {section.label}
        </td>
        <td className="px-3 py-1 text-right text-xs text-gray-500">{formatCurrency(section.brut)}</td>
        <td className="px-3 py-1 text-right text-xs text-gray-500">{formatCurrency(section.amort)}</td>
        <td className="px-3 py-1 text-right font-semibold text-green-900 text-sm">
          {formatCurrency(section.net)}
        </td>
        <td className="px-3 py-1"></td>
      </tr>
    </>
  );
}

function ActifSubsectionRows({
  subsection,
  startDate,
  endDate,
  showN1,
}: {
  subsection: ActifSubsection;
  startDate: string;
  endDate: string;
  showN1: boolean;
}) {
  return (
    <>
      <tr>
        <td className="px-3 py-1 pl-6 italic text-sm text-gray-700" colSpan={5}>
          {subsection.label}
        </td>
      </tr>
      {subsection.lines
        .filter((l) => l.brut !== 0 || l.amort !== 0 || l.net !== 0 || l.accounts.length > 0)
        .map((line) => (
          <ActifLineRows
            key={line.key}
            line={line}
            startDate={startDate}
            endDate={endDate}
            showN1={showN1}
          />
        ))}
    </>
  );
}

function ActifLineRows({
  line,
  startDate,
  endDate,
  showN1,
}: {
  line: ActifLine;
  startDate: string;
  endDate: string;
  showN1: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const hasAccounts = line.accounts.length > 0;
  const n1 = showN1 ? N1_ACTIF_LINES[line.key] : undefined;
  return (
    <>
      <tr
        className={`${hasAccounts ? "hover:bg-gray-50 cursor-pointer" : ""} border-b border-gray-100`}
        onClick={hasAccounts ? () => setExpanded((e) => !e) : undefined}
      >
        <td className="px-3 py-1 pl-10 text-sm text-gray-800">
          {hasAccounts ? (
            <span className="inline-block w-3 text-gray-400">{expanded ? "▼" : "▶"}</span>
          ) : (
            <span className="inline-block w-3" />
          )}
          <span className="ml-1">{line.label}</span>
        </td>
        <td className="px-3 py-1 text-right text-xs text-gray-500">{formatCurrency(line.brut)}</td>
        <td className="px-3 py-1 text-right text-xs text-gray-500">{formatCurrency(line.amort)}</td>
        <td className="px-3 py-1 text-right text-sm font-medium">{formatCurrency(line.net)}</td>
        <td className="px-3 py-1 text-right text-xs text-gray-600">
          {n1 !== undefined ? formatCurrency(n1) : "-"}
        </td>
      </tr>
      {expanded &&
        line.accounts.map((acc) => (
          <AccountDrilldown
            key={acc.accountCode}
            accountCode={acc.accountCode}
            accountLabel={
              acc.kind === "amort" ? `${acc.accountLabel} (amort.)` : acc.accountLabel
            }
            total={acc.total}
            startDate={startDate}
            endDate={endDate}
          />
        ))}
    </>
  );
}

function PassifSectionRows({
  section,
  startDate,
  endDate,
  showN1,
}: {
  section: PassifSection;
  startDate: string;
  endDate: string;
  showN1: boolean;
}) {
  return (
    <>
      <tr className="bg-blue-50">
        <td className="px-3 py-1.5 font-bold text-blue-900 uppercase text-sm" colSpan={3}>
          {section.label}
        </td>
      </tr>
      {section.lines
        .filter((l) => l.total !== 0 || l.accounts.length > 0)
        .map((line) => (
          <PassifLineRows
            key={line.key}
            line={line}
            startDate={startDate}
            endDate={endDate}
            showN1={showN1}
          />
        ))}
      <tr className="bg-blue-50/50 border-t border-blue-200">
        <td className="px-3 py-1 pl-3 font-semibold text-blue-900 text-sm">
          Total {section.label}
        </td>
        <td className="px-3 py-1 text-right font-semibold text-blue-900 text-sm">
          {formatCurrency(section.total)}
        </td>
        <td className="px-3 py-1"></td>
      </tr>
    </>
  );
}

function PassifLineRows({
  line,
  startDate,
  endDate,
  showN1,
}: {
  line: PassifLine;
  startDate: string;
  endDate: string;
  showN1: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const hasAccounts = line.accounts.length > 0;
  const n1 = showN1 ? N1_PASSIF_LINES[line.key] : undefined;
  return (
    <>
      <tr
        className={`${hasAccounts ? "hover:bg-gray-50 cursor-pointer" : ""} border-b border-gray-100`}
        onClick={hasAccounts ? () => setExpanded((e) => !e) : undefined}
      >
        <td className="px-3 py-1 pl-8 text-sm text-gray-800">
          {hasAccounts ? (
            <span className="inline-block w-3 text-gray-400">{expanded ? "▼" : "▶"}</span>
          ) : (
            <span className="inline-block w-3" />
          )}
          <span className="ml-1">{line.label}</span>
        </td>
        <td className="px-3 py-1 text-right text-sm font-medium">{formatCurrency(line.total)}</td>
        <td className="px-3 py-1 text-right text-xs text-gray-600">
          {n1 !== undefined ? formatCurrency(n1) : "-"}
        </td>
      </tr>
      {expanded &&
        line.accounts.map((acc) => (
          <AccountDrilldown
            key={acc.accountCode}
            accountCode={acc.accountCode}
            accountLabel={acc.accountLabel}
            total={acc.total}
            startDate={startDate}
            endDate={endDate}
            balanceDirection="credit"
          />
        ))}
    </>
  );
}

const VARIANT_CLASSES = {
  green: { bg: "bg-green-50 border-green-200", label: "text-green-700", value: "text-green-800" },
  red: { bg: "bg-red-50 border-red-200", label: "text-red-700", value: "text-red-800" },
  blue: { bg: "bg-blue-50 border-blue-200", label: "text-blue-700", value: "text-blue-800" },
} as const;

function SummaryCard({
  variant,
  label,
  value,
}: {
  variant: keyof typeof VARIANT_CLASSES;
  label: string;
  value: string;
}) {
  const v = VARIANT_CLASSES[variant];
  return (
    <div className={`border rounded-lg p-4 ${v.bg}`}>
      <div className={`text-sm font-medium ${v.label}`}>{label}</div>
      <div className={`text-2xl font-bold ${v.value}`}>{value}</div>
    </div>
  );
}
